import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { OverlayIconButton } from "@/components/shared/OverlayIconButton";
import { useUnreadCount } from "@/hooks/useUnreadCount";
import { getTabIdFromPath } from "@/routes/routeObjects";
import { useMenu } from "./MenuContext";

/** The native menu mark (BrowseChrome.swift `MenuIcon`): two strokes, the top one shortened beside an unread dot. */
function MenuIcon({ unread }: { unread: boolean }) {
  return (
    <span className="relative inline-flex h-[18px] w-[22px]">
      <svg width="22" height="18" viewBox="0 0 22 18" fill="none" aria-hidden="true">
        <path d={`M2 4H${unread ? 10 : 20}M2 12H14`} stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      {unread && <span className="absolute right-0 top-px h-[7px] w-[7px] rounded-full bg-brand-accent" />}
    </span>
  );
}

/**
 * Opens the menu from a root page (native `AccountToolbar`), in the slot where a
 * pushed page shows Back. It is the app's round icon button: the outline circle on
 * the page, or the overlay circle over media and the map. Renders nothing outside
 * the signed-in shell or on a pushed page.
 */
export function MenuButton({ overMedia = false, className }: { overMedia?: boolean; className?: string }) {
  const menu = useMenu();
  const location = useLocation();
  const unread = useUnreadCount() > 0;
  if (!menu || menu.docked || !getTabIdFromPath(location.pathname)) return null;

  const props = {
    onClick: menu.openMenu,
    "aria-label": "Menu",
    "aria-description": unread ? "Unread notifications" : undefined,
    "aria-expanded": menu.open,
  };

  return overMedia ? (
    <OverlayIconButton {...props} className={className}>
      <MenuIcon unread={unread} />
    </OverlayIconButton>
  ) : (
    <Button
      type="button"
      variant="secondary"
      size="icon"
      {...props}
      className={cn("shrink-0 [&_svg]:size-auto", className)}
    >
      <MenuIcon unread={unread} />
    </Button>
  );
}
